/**
 * src/context/SnackbarContext.tsx
 *
 * Global snackbar notification system (WP-ATTICDEV-013-SNACKBAR).
 * US-001: AC-001, AC-002, AC-003, AC-004
 *
 * Design notes:
 * - Single active slot: showSnackbar always replaces the current snackbar.
 * - Timer is cleared-and-reset on every showSnackbar call (AC-002 timer reset).
 * - Calling useSnackbar() outside SnackbarProvider logs console.error and
 *   returns no-op functions (sentinel pattern — no throw at render time, AC-001).
 * - showSnackbar('') is a no-op (empty message guard, AC-001).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { haptics } from '../utils/haptics';

// ── Types ────────────────────────────────────────────────────────────────────

export type SnackbarVariant = 'success' | 'error' | 'info';

export interface SnackbarAction {
  label: string;
  onClick: () => void;
}

export interface SnackbarOptions {
  /** Visual variant controlling background colour and aria role. Default: 'info'. */
  variant?: SnackbarVariant | undefined;
  /** Auto-dismiss delay in ms. Default: 4000. */
  duration?: number | undefined;
  /** Optional action button rendered inside the snackbar. */
  action?: SnackbarAction | undefined;
}

/** Internal entry stored in state. `key` is a timestamp used to detect replacements. */
export interface SnackbarEntry {
  message: string;
  variant: SnackbarVariant;
  duration: number;
  action?: SnackbarAction | undefined;
  /** Monotonically increasing timestamp; changes on every showSnackbar call. */
  key: number;
}

interface SnackbarContextValue {
  /** Currently active snackbar, or null when none is shown. */
  snackbar: SnackbarEntry | null;
  /** Show a snackbar. Replaces any existing snackbar and resets its timer. */
  showSnackbar: (message: string, options?: SnackbarOptions) => void;
  /** Dismiss the current snackbar immediately. */
  dismiss: () => void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_DURATION = 4000;

// ── Context ──────────────────────────────────────────────────────────────────

/** Sentinel: null means "no SnackbarProvider in tree." */
const SnackbarContext = createContext<SnackbarContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

interface SnackbarProviderProps {
  children: React.ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [snackbar, setSnackbar] = useState<SnackbarEntry | null>(null);
  /**
   * useRef stores the auto-dismiss timer handle. Using a ref (not state)
   * prevents re-renders on timer updates and keeps dismiss() callback stable.
   */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Stable dismiss callback — safe to use in SnackbarHost without stale closures. */
  const dismiss = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setSnackbar(null);
  }, []);

  const showSnackbar = useCallback(
    (message: string, options?: SnackbarOptions) => {
      // Empty-message guard (AC-001).
      if (!message.trim()) return;

      // Clear existing timer before replacing — guarantees single-instance and
      // full timer reset even when same message is passed (AC-002).
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const variant = options?.variant ?? 'info';
      const duration = options?.duration ?? DEFAULT_DURATION;

      // Tactile beat on the outcome. Fires here (not in SnackbarHost, which is
      // currently not mounted) so success/error confirmations still feel
      // physical app-wide. No-op under reduced-motion / unsupported platforms.
      if (variant === 'success') haptics.success();
      else if (variant === 'error') haptics.warn();

      setSnackbar({
        message,
        variant,
        duration,
        action: options?.action,
        // Timestamp key: changes on every call so SnackbarHost can detect replacements.
        key: Date.now(),
      });

      // Schedule auto-dismiss (AC-003).
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setSnackbar(null);
      }, duration);
    },
    [],
  );

  return (
    <SnackbarContext.Provider value={{ snackbar, showSnackbar, dismiss }}>
      {children}
    </SnackbarContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the snackbar controls. Safe to call anywhere inside SnackbarProvider.
 *
 * If called outside SnackbarProvider, logs console.error and returns no-op
 * functions so callers do not crash (AC-001 out-of-provider guard).
 */
export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);

  if (ctx === null) {
    // eslint-disable-next-line no-console
    console.error(
      '[useSnackbar] Must be called within <SnackbarProvider>. ' +
        'No snackbar will render. Wrap your app or component tree with <SnackbarProvider>.',
    );
    return {
      snackbar: null,
      showSnackbar: (_message: string, _options?: SnackbarOptions) => undefined,
      dismiss: () => undefined,
    };
  }

  return ctx;
}
